import { useSelector } from 'react-redux';
import TitleHeader from '../../components/MasterComponent/TitleHeader/TitleHeader';
import { SelectedAccount } from '../../components/SelectAccount/SelectAccount';
import { CLOUD_PROVIDER_SERVICES } from '../../utils/constants';
import { useFetchData } from '../../hooks/useFetchData';
import { config } from './config';
import { exportToFile } from '../../utils/formateXLSX';
import { accountState, RootState } from '../../store';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../CostBreakup/cost-breakup.css';
import Header from '../../components/CostBreakup/Header';
import { MultilevelHeaderTable } from '../../components/CostBreakup/MultilevelHeaderTable';
import { ALL_VALUE } from '../Invoice/config';
import {
  SUMMARY_TABS,
  SummaryTabs,
  SUMMARY_SERVICE_REPORT_MAP,
} from './reservationTabs';
import { SUMMARY_SUMMABLE_COLUMNS_BY_TAB } from './reservationTableColumns';
import CustomSnackBar from '../../components/MasterComponent/CustomSnackbar/CustomSnackBar';

const Summary: React.FC<any> = () => {
  const selectedAccountInfo = useSelector(accountState);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [summaryData, setSummaryData] = useState([]);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'info' | 'success' | 'error' | 'warning'
  >('info');
  const [selectedAccountId, setSelectedAccountId] = React.useState<any>(
    selectedAccountInfo.selectedAccountId
  );
  const [selectedTab, setSelectedTab] = useState<SummaryTabs>(
    SUMMARY_TABS[0]
  );
  const selectedMonth = useSelector(
    (state: RootState) => state.month.selectedMonth
  );
  const [selectedMonthName, selectedYear] = selectedMonth.split(' ');
  const monthNumber =
    new Date(`${selectedMonthName} 1, ${selectedYear}`).getMonth() + 1;

  const currentTabRef = useRef<SummaryTabs>(selectedTab);
  const getCacheKey = (tab: SummaryTabs, accountId: string, month: string) =>
  `${tab}_${accountId}_${month}`;
  const cacheRef = useRef<Map<string, any>>(new Map());
  const formattedMonth = monthNumber.toString().padStart(2, '0');
  const [sortColumn, setSortColumn] = useState<string>('');
  const reqBodyRef = useRef({
    startDate: `${selectedYear}-${formattedMonth}-01`,
    endDate: `${selectedYear}-${formattedMonth}-${new Date(
      parseInt(selectedYear),
      monthNumber,
      0
    ).getDate()}`,
    service: SUMMARY_SERVICE_REPORT_MAP[selectedTab],
    ...(selectedAccountId !== ALL_VALUE
      ? { accountId: selectedAccountId }
      : {}),
  });

  const { fetch, data, loader: isSubmitting } = useFetchData({
    fetchData: () => config.getSummaryReport(reqBodyRef.current),
    fetchOnMount: false,
  });

  const handleSnackBarClose = () => {
    setSnackBarOpen(false);
  };

  const onAccountChange = (data: any) => {
    if (data.cloudType === CLOUD_PROVIDER_SERVICES.AWS)
      setSelectedAccountId(data.accountId);
  };

  // Handle sorting
  const handleSort = (column: string) => {
    const newDirection =
      sortColumn === column && sortDirection === 'desc' ? 'asc' : 'desc';

    setSortColumn(column);
    setSortDirection(newDirection);
  };

  const handleTabClick = (newTab: SummaryTabs) => {
    setSelectedTab(newTab);
  };

  const handleExcelDownload = () => {
    reqBodyRef.current = {
      startDate: `${selectedYear}-${formattedMonth}-01`,
      endDate: `${selectedYear}-${formattedMonth}-${new Date(
        parseInt(selectedYear),
        monthNumber,
        0
      ).getDate()}`,
      service: SUMMARY_SERVICE_REPORT_MAP[selectedTab],
      ...(selectedAccountId !== ALL_VALUE
        ? { accountId: selectedAccountId }
        : {}),
    };
    fetchSummaryReport();
  };

  const {
    fetch: fetchSummaryReport,
    data: fetchSummaryReportDownloadStatus,
    loader: isDownloadLoading,
    error: fetchSummaryReportDownloadError,
  } = useFetchData({
    fetchData: () => config.downloadSummaryReport(reqBodyRef.current),
    fetchOnMount: false,
  });

  const sortedServiceEntries = useMemo(() => {
    // Convert array to entries format expected by MultilevelHeaderTable
    const serviceEntries = summaryData.map(
      (item: any, index: number) => [`row_${index}`, item]
    ) as [string, Record<string, any>][];

    if (!sortColumn) return serviceEntries;

    return [...serviceEntries].sort((a, b) => {
      let valueA = a[1][sortColumn];
      let valueB = b[1][sortColumn];

      // Null / undefined fallback
      if (valueA === undefined || valueA === null) valueA = '';
      if (valueB === undefined || valueB === null) valueB = '';

      // If both are numbers
      if (!isNaN(Number(valueA)) && !isNaN(Number(valueB))) {
        return sortDirection === 'asc'
          ? Number(valueA) - Number(valueB)
          : Number(valueB) - Number(valueA);
      }

      // Otherwise compare as strings
      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      return sortDirection === 'asc'
        ? strA.localeCompare(strB)
        : strB.localeCompare(strA);
    });
  }, [summaryData, sortColumn, sortDirection]);

  const columns = useMemo(() => {
    if (!summaryData || summaryData.length === 0) return [];

    // Collect all unique keys across all entries
    const allKeysSet = new Set<string>();
    for (const item of summaryData) {
      Object.keys(item).forEach((key) => allKeysSet.add(key));
    }

    return Array.from(allKeysSet).map((key) => ({
      key,
      label: key, // Use the display key directly since it's already formatted
    }));
  }, [summaryData]);

  const summableColumns = useMemo(() => {
    const columns = SUMMARY_SUMMABLE_COLUMNS_BY_TAB[selectedTab] ?? [];
    return columns;
  }, [selectedTab]);

  useEffect(() => {
    const cacheKey = getCacheKey(selectedTab, selectedAccountId, selectedMonth);
    reqBodyRef.current = {
      startDate: `${selectedYear}-${formattedMonth}-01`,
      endDate: `${selectedYear}-${formattedMonth}-${new Date(
        parseInt(selectedYear),
        monthNumber,
        0
      ).getDate()}`,
      service: SUMMARY_SERVICE_REPORT_MAP[selectedTab],
      ...(selectedAccountId !== ALL_VALUE
        ? { accountId: selectedAccountId }
        : {}),
    };
    if (cacheRef.current.has(cacheKey)) {
      setSummaryData(cacheRef.current.get(cacheKey));
    } else {
      setSummaryData([]);
      fetch();
    }
  }, [selectedTab, selectedAccountId, selectedMonth]);

  let isCanceled = false;

  useEffect(() => {
    if (!isSubmitting && !isCanceled) {
      const cacheKey = getCacheKey(selectedTab, selectedAccountId, selectedMonth);
      if (reqBodyRef.current.service === SUMMARY_SERVICE_REPORT_MAP[currentTabRef.current]) {
        cacheRef.current.set(cacheKey, data ?? []);
        setSummaryData(data ?? []);
      }
    }

    return () => {
      isCanceled = true;
    };  
  }, [data, isSubmitting, selectedTab, selectedAccountId, selectedMonth]);

  useEffect(() => {
    cacheRef.current.clear();
  }, [selectedAccountId, selectedMonth]);


  useEffect(() => {
    currentTabRef.current = selectedTab;
  }, [selectedTab]);

  useEffect(() => {
    if (!isDownloadLoading) {
      if (fetchSummaryReportDownloadError) {
        const errorMessage =
          fetchSummaryReportDownloadError?.error?.message ||
          'An error occurred while exporting.';
        setSnackBarMessage(errorMessage);
        setSnackBarOpen(true);
        setSnackbarSeverity('error');
      } else if (fetchSummaryReportDownloadStatus) {
        const {
          fileBuffer,
          fileName,
          message,
        } = fetchSummaryReportDownloadStatus;

        if (fileBuffer && fileName) {
          exportToFile(fileBuffer, fileName);
        } else if (message === 'No data available to export') {
          setSnackBarMessage('No data available to export.');
          setSnackBarOpen(true);
          setSnackbarSeverity('info');
        }
      }
    }
  }, [
    isDownloadLoading,
    fetchSummaryReportDownloadStatus,
    fetchSummaryReportDownloadError,
  ]);

  return (
    <div className="app-container">
      <CustomSnackBar
        message={snackBarMessage}
        onClose={handleSnackBarClose}
        open={snackBarOpen}
        severity={snackbarSeverity}
      />
      <TitleHeader
        extraContent={<SelectedAccount onAccountChange={onAccountChange} />}
      >
        Summary
      </TitleHeader>
      <Header
        tabs={[...SUMMARY_TABS]}
        selectedTab={selectedTab}
        onTabChange={handleTabClick}
        showMonthFilter
        showExcelDownload
        onDownload={handleExcelDownload}
        isDownloading={isDownloadLoading}
      />
      <MultilevelHeaderTable
        loading={isSubmitting}
        sortedEntries={sortedServiceEntries}
        onSort={handleSort}
        columns={columns}
        summableColumns={summableColumns}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
      />
    </div>
  );
};

export default Summary;
